import { RowCrudStatus } from "../../common/types";
import {
  WorkAuthorPublisher,
  WorkAuthorPublisher_CreateInput,
  WorkAuthorPublisher_UpdateInput,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { publisherDisplayId } from "../../publisher/store/PublisherCommonStore";

export type WorkAuthorPublisherValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  __publisher: string;
  showPublisher: { displayId: string; id: string } | null;
  percentage: number;
  durationInYears: number | undefined;
  autoRenewalYears: number | undefined;
};

export type WorkAuthorPublisherErrorsType = {
  publisher?: { message: string; fieldLabel: string };
  percentage?: { message: string; fieldLabel: string };
  durationInYears?: { message: string; fieldLabel: string };
  autoRenewalYears?: { message: string; fieldLabel: string };
};

export function WorkAuthorPublisherValuesInit(): WorkAuthorPublisherValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    __publisher: "",
    showPublisher: null,
    percentage: 0,
    durationInYears: undefined,
    autoRenewalYears: undefined,
  };
}

export type WorkAuthorPublisherType =
  | WorkAuthorPublisher
  | WorkAuthorPublisherValuesType;

export function workAuthorPublisherDisplayId(
  source: Partial<WorkAuthorPublisher>,
  _langStore: LangStore
) {
  return (
    (source.publisher
      ? publisherDisplayId(source.publisher, _langStore)
      : _langStore.emptyStringSymbol) +
    " " +
    (source.percentage?.toString() ?? _langStore.emptyStringSymbol) +
    "%"
  );
}

export function getWorkAuthorPublisherValues(
  source: WorkAuthorPublisherType,
  _langStore: LangStore,
  _duplicated: boolean = false
): WorkAuthorPublisherValuesType {
  const entityValues = WorkAuthorPublisherValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : workAuthorPublisherDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.showPublisher =
    "publisher" in source && source.publisher
      ? {
          id: source.publisher.id,
          displayId: publisherDisplayId(source.publisher, _langStore),
        }
      : "showPublisher" in source && source.showPublisher
      ? {
          id: source.showPublisher.id,
          displayId: source.showPublisher.displayId,
        }
      : null;
  entityValues.__publisher = entityValues.showPublisher?.displayId || "";
  entityValues.percentage = Number(source.percentage) || 0;
  entityValues.durationInYears =
    (source.durationInYears as unknown) === "" ||
    source.durationInYears === undefined ||
    source.durationInYears === null
      ? undefined
      : source.durationInYears;
  entityValues.autoRenewalYears =
    (source.autoRenewalYears as unknown) === "" ||
    source.autoRenewalYears === undefined ||
    source.autoRenewalYears === null
      ? undefined
      : source.autoRenewalYears;
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getWorkAuthorPublisherUpdateInput(
  values: WorkAuthorPublisherValuesType
): WorkAuthorPublisher_UpdateInput {
  return {
    id: values.id,
    version_: values.version_,
    publisherId: values.showPublisher?.id || "",
    percentage: values.percentage,
    durationInYears:
      values.durationInYears === undefined ? null : values.durationInYears,
    autoRenewalYears:
      values.autoRenewalYears === undefined ? null : values.autoRenewalYears,
  };
}

export function getWorkAuthorPublisherCreateInput(
  values: WorkAuthorPublisherValuesType
): WorkAuthorPublisher_CreateInput {
  return {
    publisherId: values.showPublisher?.id || "",
    percentage: values.percentage,
    durationInYears:
      values.durationInYears === undefined ? null : values.durationInYears,
    autoRenewalYears:
      values.autoRenewalYears === undefined ? null : values.autoRenewalYears,
  };
}
